import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ImpersonatedUser {
  id: string;
  email: string;
  full_name: string;
}

export const useImpersonation = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Validate session function (reusable)
  const validateSession = async (sessionIdToValidate: string): Promise<boolean> => {
    try {
      const { data: session, error } = await supabase
        .from("impersonation_sessions")
        .select("id, impersonated_user_id, is_active, ended_at")
        .eq("id", sessionIdToValidate)
        .eq("is_active", true)
        .is("ended_at", null)
        .single();

      if (error || !session) {
        return false;
      }

      return true;
    } catch (error) {
      console.error("Session validation error:", error);
      return false;
    }
  };

  useEffect(() => {
    // Check for active impersonation session on mount and validate server-side
    const checkSession = async () => {
      const sessionData = sessionStorage.getItem("impersonation_session");
      if (!sessionData) return;

      try {
        const data = JSON.parse(sessionData);
        
        // Validate session is still active on server
        const { data: session, error } = await supabase
          .from("impersonation_sessions")
          .select("id, impersonated_user_id, is_active, ended_at")
          .eq("id", data.sessionId)
          .eq("is_active", true)
          .is("ended_at", null)
          .single();

        if (error || !session) {
          // Session invalid - clear it
          sessionStorage.removeItem("impersonation_session");
          return;
        }

        // Session valid - fetch fresh user data
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("id, email, full_name")
          .eq("id", session.impersonated_user_id)
          .single();

        if (profileError || !profile) {
          sessionStorage.removeItem("impersonation_session");
          return;
        }

        setIsImpersonating(true);
        setImpersonatedUser(profile);
        setSessionId(data.sessionId);
      } catch (error) {
        console.error("Failed to validate impersonation session:", error);
        sessionStorage.removeItem("impersonation_session");
      }
    };

    checkSession();

    // Periodic validation: Check session every 30 seconds while impersonating
    const validationInterval = setInterval(async () => {
      const sessionData = sessionStorage.getItem("impersonation_session");
      if (!sessionData) {
        clearInterval(validationInterval);
        return;
      }

      try {
        const data = JSON.parse(sessionData);
        const isValid = await validateSession(data.sessionId);
        
        if (!isValid) {
          // Session invalidated server-side - clean up client state
          sessionStorage.removeItem("impersonation_session");
          setIsImpersonating(false);
          setImpersonatedUser(null);
          setSessionId(null);
          clearInterval(validationInterval);
          
          toast.error("Impersonation session has been ended by another admin");
          window.location.reload();
        }
      } catch (error) {
        console.error("Periodic session validation error:", error);
      }
    }, 30000); // Validate every 30 seconds

    return () => clearInterval(validationInterval);
  }, []);

  const startImpersonation = async (userId: string) => {
    try {
      // Get user details
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, email, full_name")
        .eq("id", userId)
        .single();

      if (profileError || !profile) {
        toast.error("Failed to load user profile");
        return false;
      }

      // Create impersonation session
      const { data: session, error: sessionError } = await supabase
        .from("impersonation_sessions")
        .insert([{
          admin_user_id: (await supabase.auth.getUser()).data.user?.id || "",
          impersonated_user_id: userId,
          ip_address: null,
          user_agent: navigator.userAgent,
        }])
        .select()
        .single();

      if (sessionError || !session) {
        toast.error("Failed to create impersonation session");
        return false;
      }

      // Store only session ID in sessionStorage (clears on browser close)
      const impersonationData = {
        sessionId: session.id,
        startedAt: new Date().toISOString(),
      };
      sessionStorage.setItem("impersonation_session", JSON.stringify(impersonationData));

      setIsImpersonating(true);
      setImpersonatedUser(profile);
      setSessionId(session.id);

      toast.success(`Now impersonating ${profile.full_name}`);
      return true;
    } catch (error) {
      console.error("Impersonation error:", error);
      toast.error("Failed to start impersonation");
      return false;
    }
  };

  const stopImpersonation = async () => {
    try {
      if (sessionId) {
        // End the session
        await supabase
          .from("impersonation_sessions")
          .update({
            ended_at: new Date().toISOString(),
            is_active: false,
          })
          .eq("id", sessionId);
      }

      sessionStorage.removeItem("impersonation_session");
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setSessionId(null);

      toast.success("Impersonation session ended");
      
      // Reload to return to admin view
      window.location.reload();
    } catch (error) {
      console.error("Error ending impersonation:", error);
      toast.error("Failed to end impersonation session");
    }
  };

  const logAction = async (action: string, details: any) => {
    if (!sessionId || !isImpersonating) return;

    try {
      // Validate session is still active before logging
      const isValid = await validateSession(sessionId);
      if (!isValid) {
        toast.error("Impersonation session has expired");
        stopImpersonation();
        return;
      }

      const { data: session } = await supabase
        .from("impersonation_sessions")
        .select("actions_log")
        .eq("id", sessionId)
        .single();

      if (session) {
        const currentLog = Array.isArray(session.actions_log) ? session.actions_log : [];
        const newLog = [
          ...currentLog,
          {
            timestamp: new Date().toISOString(),
            action,
            details,
          },
        ];

        await supabase
          .from("impersonation_sessions")
          .update({ actions_log: newLog as any })
          .eq("id", sessionId);
      }
    } catch (error) {
      console.error("Failed to log action:", error);
    }
  };

  return {
    isImpersonating,
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
    logAction,
  };
};
